// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { mdiFilter } from "@mdi/js";
import { Button, Input } from "antd";
import styled from "styled-components";
import { UNIT_IDS } from "types/units";
import { v4 as uuidv4 } from "uuid";

import { useLengthConversion } from "hooks/useUnitConversion";

import { SHAPEFILE_MATCH_MODE } from "models";

import SegmentedToggle, { SegmentedVariant } from "components/shared/SegmentedToggle";

import {
  resolveShapefileBufferUnit,
  shapefileBufferUnitPair,
  storedShapefileBufferUnit
} from "../filter/util/shapefileBufferUnit";
import { rememberShapefileBufferUnit } from "../filter/util/shapefileBufferUnitStore";
import {
  MAX_SHAPEFILE_BUFFER_METRES,
  METRES_PER_LABEL_UNIT,
  SHAPEFILE_BUFFER_QUANTUM_METRES,
  SHAPEFILE_FILTER_OFF,
  ShapefileBufferDisplay,
  ShapefileBufferLabelUnit,
  ShapefileFilterDraft,
  ShapefileFilterMode,
  canApplyShapefileDraft
} from "../filter/util/shapefileFilterPredicate";
import { useShapefileFilterMode } from "./hooks";

const FILTER_MODE_OPTIONS: ReadonlyArray<{
  mode: ShapefileFilterMode;
  label: string;
}> = [
  { mode: SHAPEFILE_FILTER_OFF, label: "Off" },
  { mode: SHAPEFILE_MATCH_MODE.Intersecting, label: "Intersecting" },
  { mode: SHAPEFILE_MATCH_MODE.ContainedWithin, label: "Contained within" }
];

/**
 * Metres per unit the buffer can be typed in, spelled out rather than run
 * through the unit converter: that converter hands back the number it was
 * given when its engine has not loaded, which would quietly turn 1 mile into
 * 1 metre. It is the label's own table, shared, so a distance typed here and
 * the same distance read back on the chip are converted by one set of numbers.
 */
const UNIT_METRES: Record<string, number> = METRES_PER_LABEL_UNIT;

/**
 * The furthest a distance can be typed in each unit: round numbers that all
 * sit at or under the thousand miles MAX_SHAPEFILE_BUFFER_METRES stands for,
 * so the number in the box is exactly the one that applies rather than one
 * quietly cut down on its way to the filter. The cap the commit puts on the
 * metres it reads stays behind them as the backstop, for a distance that
 * reaches the box some other way — a unit picked beside a number already
 * there, or a buffer applied from elsewhere.
 *
 * Round and under is the deliberate trade, and it was settled twice: the
 * alternative — rounding each unit up to the next round number and leaving the
 * commit to cut down whatever is typed past the cap — was turned down both
 * times, because a number the box took and then quietly filtered at some
 * shorter distance reads as a bug. The price is that a unit stops short of the
 * cap wherever the round number under it is well short: feet top out at
 * 5,000,000, about 947 miles, so the last fifty-odd miles have to be asked for
 * in another unit.
 */
const UNIT_MAX: Record<string, number> = {
  [UNIT_IDS.METERS]: 1600000,
  [UNIT_IDS.KILOMETERS]: 1600,
  [UNIT_IDS.FEET]: 5000000,
  [UNIT_IDS.MILES]: 1000
};

/**
 * How far one click of the box's own arrows moves the number in each unit.
 * Whole units for the large ones, ten for the small ones — the steps the
 * owner asked for. A metre step is the quantum itself. A step of ten feet is
 * 3.05 m, under the SHAPEFILE_BUFFER_QUANTUM_METRES the filter measures in,
 * so one ft click can land in the same ten-metre bucket as the last and only
 * the second click moves the filter; the owner took that trade for the round
 * number.
 */
const UNIT_STEP: Record<string, number> = {
  [UNIT_IDS.METERS]: SHAPEFILE_BUFFER_QUANTUM_METRES,
  [UNIT_IDS.KILOMETERS]: 1,
  [UNIT_IDS.FEET]: 10,
  [UNIT_IDS.MILES]: 1
};

/** What the box says while nothing is being filtered further than its outline. */
const NO_BUFFER = 0;

/**
 * The buffer row takes two slots: the number and the unit switch. Both need
 * one — the picker's own Tab handler swallows the key before focus can reach a
 * control that is not a slot, so anything left out of this count cannot be
 * reached by keyboard at all. Both are filled whatever the mode reads, since
 * the row is always on screen; while nothing is filtering the two controls
 * report themselves disabled and the keyboard zone steps over them.
 */
const BUFFER_TOOLBAR_SLOT_COUNT = 2;

/** Where each of the buffer row's controls sits, after the mode segments. */
const BUFFER_INPUT_SLOT = FILTER_MODE_OPTIONS.length;
const BUFFER_UNIT_SLOT = FILTER_MODE_OPTIONS.length + 1;

/**
 * How long the buffer box waits after the last change before re-displaying
 * itself on its own. Staging happens on every keystroke (there is nothing to
 * lose by staging, unlike running the filter), so this pause no longer gates
 * a write — it only gives the box a moment to settle before it stamps the cap
 * or re-says the number, the way the arrows' own stepping would otherwise
 * trigger that mid-click.
 */
const BUFFER_COMMIT_PAUSE_MS = 500;

/** The Filter button takes one slot of its own, appended after the buffer row's. */
const APPLY_TOOLBAR_SLOT_COUNT = 1;

/** Where the Filter button sits, after the mode segments and the buffer row. */
const APPLY_SLOT = FILTER_MODE_OPTIONS.length + BUFFER_TOOLBAR_SLOT_COUNT;

/** Number of toolbar slots this block reserves. */
export const SHAPEFILE_FIELD_OPTIONS_TOOLBAR_COUNT =
  FILTER_MODE_OPTIONS.length + BUFFER_TOOLBAR_SLOT_COUNT + APPLY_TOOLBAR_SLOT_COUNT;

/**
 * What the filter carries for a typed distance: capped, and on the same
 * ten-metre step the backend measures at, so what is stored is the distance
 * the filter runs at. A distance is never stepped down to nothing, though —
 * an entry under one step lands on one step, since somebody typing 1 ft is
 * asking for the smallest buffer there is rather than for none. Zero is the
 * one way to ask for none, and it is left as it is. The buffer invariant in
 * components/filter/CONTEXT.md has what the backend does with each.
 */
function toMetres(value: Maybe<number>, unit: string): number {
  const metres = (value ?? 0) * (UNIT_METRES[unit] ?? 1);
  if (!(metres > 0)) {
    return NO_BUFFER;
  }
  const stepped =
    Math.round(metres / SHAPEFILE_BUFFER_QUANTUM_METRES) *
    SHAPEFILE_BUFFER_QUANTUM_METRES;
  return Math.min(
    Math.max(SHAPEFILE_BUFFER_QUANTUM_METRES, stepped),
    MAX_SHAPEFILE_BUFFER_METRES
  );
}

/** The furthest the box takes in a unit, in metres for a unit it does not know. */
function unitMax(unit: string): number {
  return UNIT_MAX[unit] ?? UNIT_MAX[UNIT_IDS.METERS];
}

/** What one arrow click moves, in metres for a unit the box does not know. */
function unitStep(unit: string): number {
  return UNIT_STEP[unit] ?? UNIT_STEP[UNIT_IDS.METERS];
}

/**
 * The same distance back in the box's unit, to two decimals — the precision
 * every other converted length is shown at, and close enough that no number
 * the box shows trails off into the float it was divided into.
 */
function fromMetres(metres: number, unit: string): number {
  return Number((metres / (UNIT_METRES[unit] ?? 1)).toFixed(2));
}

/**
 * The distance out of some text: its digits, and at most one decimal point.
 * Anything else pasted in — a unit, a comma, a sign — is dropped rather than
 * left in the box, which shows whatever it is handed until focus leaves it.
 */
function numericText(text: string): string {
  const [whole, ...rest] = text.replace(/[^\d.]/g, "").split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
}

/**
 * Keystrokes the box takes: the digits, and one decimal point. A key that
 * moves the caret or edits what is there arrives under a name of its own
 * rather than as a character, and a held modifier means a shortcut — copy,
 * paste, select all — so both go through untouched. Everything else is
 * refused as it is typed: a number box takes "e", a sign and a second point
 * as part of a number, shows them, and then reads back as empty because what
 * they spell is not one.
 *
 * A second point is refused even where the first one is selected and would be
 * typed over. A number box reports no caret and no selection at all — its
 * selectionStart and selectionEnd are null — so there is no way to tell what a
 * keystroke is about to replace. Selecting the whole box and typing a point
 * leaves it refused; any digit clears the box and the point goes in after it.
 */
function blockNonNumericKeys(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key?.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  const input = event.currentTarget;
  const typeable =
    /\d/.test(event.key) || (event.key === "." && !input.value.includes("."));
  if (!typeable) {
    event.preventDefault();
  }
}

interface ShapefileFieldOptionsProps {
  /** Toolbar index of this block's first control, relative to the category. */
  baseToolbarIndex: number;
  /** The shapefile these options are being shown for, from the tree select. */
  projectId?: string;
  shapefileId?: string;
  shapefileName?: string;
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
}

/**
 * Filtering by the shapefile the picker is browsing. The toggle owns one
 * predicate on that shapefile's boundary, but nothing about it is written the
 * moment it is touched: the mode, the buffer and the unit are all staged into
 * a component-local draft first, and the band shows that draft — not the
 * applied filter — while the two differ. Pressing **Filter** is the
 * only action in the band that writes the predicate, through the same
 * `applyDraft` (`useShapefileFilterMode`) a legend selection's reducer
 * ultimately shares, so the filter re-runs off the ordinary properties-filter
 * change and no separate watcher is needed. The draft is seeded from the
 * applied predicate on mount, re-seeded whenever the predicate's id moves —
 * undo, a saved-filter load, a View load, session restore — and discarded on
 * unmount and on a shapefile re-pick (`key={selectedShapefile}` in
 * `ShapefileCategory`); it is never persisted into a View or a saved filter.
 *
 * The buffer rides that same predicate: it is how far outside the shapefile a
 * well still counts. It stages on every keystroke, on blur, on Enter, on
 * paste and on a unit flip — never on a pause alone, since the pause is only
 * there to let the box settle before it re-says itself (the cap stamp, the
 * unit conversion) once typing stops. Its row is always on screen so that the
 * whole of what the band can do is visible at a glance, and dead while the
 * staged mode reads Off, since there is nothing for a distance past the
 * boundary to widen while nothing is matching on that boundary.
 *
 * The group-by fields listed below are unaffected: focusing one always matches
 * the shapefile the intersecting way.
 */
function ShapefileFieldOptions({
  baseToolbarIndex,
  projectId,
  shapefileId,
  shapefileName,
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef
}: ShapefileFieldOptionsProps) {
  const { mode, bufferMeters, bufferDisplay, applyDraft, predicateId } =
    useShapefileFilterMode({
      projectId,
      shapefileId,
      shapefileName
    });

  // The staged configuration: what the band shows and what the Filter button
  // writes. Seeded lazily from the applied reading so a band opened onto a
  // running filter starts clean.
  const [draft, setDraft] = useState<ShapefileFilterDraft>(() => ({
    mode,
    bufferMetres: bufferMeters,
    bufferDisplay
  }));

  // A buffer widens a filter that is already staged to match, so until a mode
  // has been picked there is nothing for one to do and the row is dead. This
  // follows the staged mode, not the applied one, so a mode can be staged and
  // its buffer typed before anything is applied.
  const filtering = draft.mode !== SHAPEFILE_FILTER_OFF;

  const optionsRef = useRef<HTMLDivElement>(null);
  const bufferRef = useRef<HTMLDivElement>(null);
  const applyRef = useRef<HTMLButtonElement>(null);
  const labelId = useMemo(() => `shapefile-filtering-label-${uuidv4()}`, []);

  const [bufferValue, setBufferValue] = useState<Maybe<number>>(NO_BUFFER);
  const [unit, setUnit] = useState<Maybe<string>>(storedShapefileBufferUnit);

  const { isReady, getDisplayUnit } = useLengthConversion();
  // Which pair is on offer follows the length unit the user reads everything
  // else in. The converter answers metres for everyone until its engine has
  // loaded, so the metric pair stands in rather than latching that. The pair
  // and the unit out of it are resolved where the label resolves them, so the
  // switch and the chip cannot end up naming different units.
  const readerUnit = isReady ? getDisplayUnit() : null;
  const unitPair = shapefileBufferUnitPair(readerUnit);
  const activeUnit = resolveShapefileBufferUnit(readerUnit, unit);

  const syncedPredicateId = useRef<Maybe<string>>("");
  const syncedUnit = useRef(activeUnit);
  // The applied buffer the box was last synced to show. Distinct from the
  // draft, which is seeded eagerly to the applied reading on mount (so a band
  // opened onto a running filter starts clean) and so cannot double as "what
  // the box has shown so far" — this starts at zero regardless of the applied
  // buffer, which is what makes the seed effect below populate the box on the
  // very first run whenever the applied buffer is non-zero.
  const syncedBufferMetres = useRef(NO_BUFFER);
  // Whether the number in the box is the staged buffer said in the box's
  // unit, rather than something anyone typed or picked a unit for. Only such a
  // number may be re-said in another unit, and staging it again says
  // nothing — in miles it would not even land back on the same metre.
  const showsAppliedBuffer = useRef(true);
  // A re-display the box has been left to make on its own, once the changes
  // stop — the cap stamp or the unit re-say, never a write to the filter.
  const pausedCommit = useRef<Maybe<ReturnType<typeof setTimeout>>>(null);
  // Whether the box is being put in order rather than typed in.
  const reconciling = useRef(false);
  // Whether the row was live last time the effect below ran, so that coming
  // back on off Off is told apart from arriving already on. A band that opens
  // onto a running filter must leave the seeding to the effect above it, which
  // puts the applied buffer back in the unit it was asked for.
  const wasFiltering = useRef(filtering);

  /** The number box itself, which is what focus and its own slot are about. */
  function bufferInputEl(): Maybe<HTMLInputElement> {
    return bufferRef.current?.querySelector<HTMLInputElement>('input[type="number"]');
  }

  /**
   * Whether the box may be written to from here right now. Every number this
   * block decides the box should say — the capped distance, the applied one,
   * the same distance re-said in another unit — lands on top of what is being
   * typed if it is written while the caret is still in the box, which is what
   * made a distance typed in feet appear to convert itself mid-word. Those
   * rewrites wait for the caret to leave, for Enter, or for a unit to be
   * picked. Staging does not wait: a stage still runs on every keystroke, only
   * the display is held back.
   */
  function mayRewriteBox(): boolean {
    return reconciling.current || document.activeElement !== bufferInputEl();
  }

  /**
   * Puts one number in the box as the applied buffer, rather than as typing.
   * Answers whether it wrote, so a caller that has to keep the draft
   * describing what the box says can tell a refused rewrite from a landed one.
   */
  function showNumberInBox(value: number): boolean {
    if (!mayRewriteBox()) {
      return false;
    }
    setBufferValue(value);
    showsAppliedBuffer.current = true;
    return true;
  }

  /** Says a distance in the box, unless the user is in the middle of typing one. */
  function showInBox(metres: number, inUnit: string): boolean {
    return showNumberInBox(fromMetres(metres, inUnit));
  }

  /**
   * The applied buffer's own wording, when it is one this box can show. The
   * unit it was asked for in has to be one of the two on offer here: a buffer
   * typed in feet by an imperial reader means nothing to a switch offering
   * kilometres and metres, and that reader gets the distance converted the way
   * they get every other length.
   */
  function saidBuffer(metres: number): Maybe<ShapefileBufferDisplay> {
    if (!bufferDisplay || metres !== bufferMeters) {
      return null;
    }
    return unitPair.includes(bufferDisplay.unit) ? bufferDisplay : null;
  }

  /**
   * Puts the applied buffer in the box: the number that was asked for, in the
   * unit it was asked for, whenever both are still sayable here — so a filter
   * reopened says the thousand feet that were typed rather than the 984.25 the
   * round trip through ten-metre steps comes back with. The switch moves to
   * that unit with it, since the number and its unit only mean anything
   * together. Anything else is the converted distance, as before.
   */
  function showAppliedBuffer(metres: number): boolean {
    const said = saidBuffer(metres);
    if (said && mayRewriteBox()) {
      syncedUnit.current = said.unit;
      setUnit(said.unit);
      return showNumberInBox(said.value);
    }
    return showInBox(metres, activeUnit);
  }

  /**
   * Puts the applied buffer in the box *and* stages it, so the draft never
   * ends up describing a distance other than the one on screen. Two readings
   * of what is staged is what let the Filter button light up over a box it would
   * not write: the box was re-said as the applied buffer while the draft still
   * carried the number the box had before, and the press then re-derived the
   * draft off the box and found nothing to do.
   */
  function stageAppliedBufferInBox(metres: number) {
    if (!showAppliedBuffer(metres)) {
      return;
    }
    setDraft((d) => ({
      ...d,
      bufferMetres: metres,
      bufferDisplay: saidBuffer(metres)
    }));
  }

  // What the box is showing right now, and the current draft, for a stage
  // that runs later than the render it was set going by. A stage fired by a
  // timer would otherwise carry whatever was in the box — or the draft — when
  // the timer was set, which is the number before the last keystroke.
  const latest = useRef({ bufferValue, activeUnit, filtering, draft });
  latest.current = { bufferValue, activeUnit, filtering, draft };

  useEffect(() => {
    const predicateMoved = syncedPredicateId.current !== predicateId;
    const previousUnit = syncedUnit.current;
    const unitMoved = previousUnit !== activeUnit;
    syncedPredicateId.current = predicateId;
    syncedUnit.current = activeUnit;

    // The applied filter moved out from under the draft — undo, a saved
    // filter or View loading, a session restore, or this band's own Apply
    // re-idding its predicate. The draft always follows, whether or not the
    // buffer happens to be the one part that changed, and it is never gated
    // on the caret guard below: that guard protects the box, not the draft.
    if (predicateMoved) {
      setDraft({ mode, bufferMetres: bufferMeters, bufferDisplay });
    }

    if (predicateMoved && bufferMeters !== syncedBufferMetres.current) {
      syncedBufferMetres.current = bufferMeters;
      showAppliedBuffer(bufferMeters);
      return;
    }
    // The box can be filled in before the unit converter has said which units
    // the user reads, which lands a render later: the same distance is said
    // again in the unit the switch is now on, rather than leaving a metre
    // count beside "ft" for the next blur to commit as feet. What may be
    // re-said is any number that is the applied buffer — one nobody has
    // touched, and one typed and staged in the unit the box carried a moment
    // ago, which is the same distance either way.
    const saysAppliedBuffer =
      showsAppliedBuffer.current ||
      toMetres(bufferValue, previousUnit) === syncedBufferMetres.current;
    if (unitMoved && saysAppliedBuffer && syncedBufferMetres.current > 0) {
      stageAppliedBufferInBox(syncedBufferMetres.current);
    }
  }, [predicateId, bufferMeters, bufferDisplay, activeUnit, bufferValue, mode]);

  useEffect(() => {
    const cameBackOn = filtering && !wasFiltering.current;
    wasFiltering.current = filtering;
    // The row is live again after a spell on Off. The buffer comes back from
    // the *applied* filter, not from whatever the draft was carrying before
    // Off cleared it: a mode flicked off and pressed back on with nothing else
    // touched has to land on clean, so that the Filter button is not offering to
    // strip a buffer the user never went near. Not from the pre-Off draft,
    // since Off is precisely what discards a staged-but-unapplied distance
    // (draft invariant 3) and carrying one back in would resurrect it.
    //
    // Reconciling, not typing: the box and the draft are put in order together
    // and neither may be left describing the other's number.
    if (cameBackOn) {
      reconciling.current = true;
      stageAppliedBufferInBox(bufferMeters);
      reconciling.current = false;
      return;
    }
    // Nothing is staged to match, so the row is dead and what was typed into
    // it goes — including a re-display waiting on a pause in the typing, and
    // whatever buffer the draft was carrying (draft invariant 3). Half-typed
    // input would otherwise sit in state and reappear, unstaged, over the next
    // filter the user turns on.
    if (filtering) {
      return;
    }
    clearTimeout(pausedCommit.current ?? undefined);
    pausedCommit.current = null;
    showsAppliedBuffer.current = true;
    setBufferValue(NO_BUFFER);
    setDraft((d) => ({ ...d, bufferMetres: NO_BUFFER, bufferDisplay: null }));
  }, [filtering]);

  /**
   * Stages a buffer distance: capped, and on the ten-metre step the filter
   * measures at, exactly as it will be written. Returns the draft it staged
   * on every path, including the one below where nothing is staged, so a
   * caller reading its return value never sees `undefined` where a draft
   * belongs (D-07) — that path is a staged Off, i.e. the "turn the filter off"
   * press, and the Filter button reads this value directly rather than state that
   * can be one render stale.
   */
  function stageBuffer(value: Maybe<number>, nextUnit: string): ShapefileFilterDraft {
    // Nothing is staged to match on the boundary, so there is nothing for a
    // distance past it to widen. The row is dead while that is true, so this
    // catches only a stage still in flight when the mode was staged off.
    if (!latest.current.filtering) {
      return latest.current.draft;
    }
    const metres = toMetres(value, nextUnit);
    // What was typed reached past the furthest a buffer goes, so the box says
    // the distance that will apply rather than the one typed over it. Staging
    // always lands, so this re-display is unconditional — there is no failed
    // write to avoid stamping the box over, the way there was when this could
    // be refused.
    if (metres === MAX_SHAPEFILE_BUFFER_METRES) {
      showInBox(metres, nextUnit);
    }
    // What the box says, kept with the distance so that the chip and the box
    // can read the number back rather than the round trip through the step
    // the filter runs on. It is the staged number, after the box has held it
    // to the unit's maximum, so it is what the user is looking at. A wording
    // that does not come back to the distance being staged — a number a cap
    // or a floor moved — is refused where the predicate is built, and
    // everything that shows the buffer converts from the metres as before.
    const said: Maybe<ShapefileBufferDisplay> =
      typeof value === "number" && isFinite(value) && value > 0
        ? { value, unit: nextUnit as ShapefileBufferLabelUnit }
        : null;
    const staged: ShapefileFilterDraft = {
      ...latest.current.draft,
      bufferMetres: metres,
      bufferDisplay: said
    };
    setDraft(staged);
    return staged;
  }

  function stageLatest(): ShapefileFilterDraft {
    return stageBuffer(latest.current.bufferValue, latest.current.activeUnit);
  }

  useEffect(
    // A re-display waiting on a pause has nothing left to show once the box
    // is gone.
    () => () => clearTimeout(pausedCommit.current ?? undefined),
    []
  );

  /**
   * Stages what is in the box right now, flushing any pause still in flight.
   * Blur and Enter stage immediately, which leaves any pause they interrupted
   * nothing to do. They are also the moments the box may be put in order: the
   * user has said they are done with the number, so whatever the stage
   * decides it should say lands here rather than under the caret. Apply
   * Filter calls this too, so a click that blurs the box on its way in stages
   * the same value the box's own blur would have.
   */
  function stageBufferNow(): ShapefileFilterDraft {
    clearTimeout(pausedCommit.current ?? undefined);
    pausedCommit.current = null;
    reconciling.current = true;
    // A box emptied out reads as no buffer, rather than staying blank over a
    // filter that has one.
    if (latest.current.bufferValue == null) {
      setBufferValue(NO_BUFFER);
    }
    const staged = stageLatest();
    reconciling.current = false;
    return staged;
  }

  function stageBufferAfterAPause() {
    clearTimeout(pausedCommit.current ?? undefined);
    pausedCommit.current = setTimeout(() => {
      pausedCommit.current = null;
      stageLatest();
    }, BUFFER_COMMIT_PAUSE_MS);
  }

  /**
   * A paste replaces what is in the box, wherever it was dropped: only the
   * distance out of the pasted text lands, and a number box hands back no
   * caret, so there is nowhere to land it beside what is already there. The
   * box is filled in by hand rather than by letting the paste through with
   * the rest stripped: what it shows is its own, and it shows what it was
   * handed. Text with no distance in it lands nothing rather than emptying
   * the box.
   */
  function onBufferPaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const digits = numericText(event.clipboardData.getData("text"));
    const value = Number(digits);
    if (digits === "" || !isFinite(value)) {
      return;
    }
    showsAppliedBuffer.current = false;
    const capped = Math.min(value, unitMax(activeUnit));
    setBufferValue(capped);
    stageBuffer(capped, activeUnit);
    stageBufferAfterAPause();
  }

  function onUnitChange(nextUnit: ShapefileBufferLabelUnit) {
    // The number stays as typed and changes meaning with the unit, which is
    // what a box reading "5" beside a switch reading "mi" says — so it is no
    // longer just the applied buffer re-said, and it is staged. Picking a
    // unit is also one of the moments the box may be put in order, since the
    // number it is now showing has to be one that unit can carry.
    showsAppliedBuffer.current = false;
    setUnit(nextUnit);
    // The seed effect's own unit-moved branch exists for a unit that changed
    // for some other reason — the length converter finishing its load — and
    // re-says the *applied* buffer over whatever staging just did here. A
    // manual pick handles its own reconciliation above, so it marks the unit
    // synced immediately rather than leaving the effect to rediscover the
    // move a render later and fight the stage that already ran.
    syncedUnit.current = nextUnit;
    // The one writer of the remembered unit, and what brings every label that
    // names it up to date: nothing is committed onto the predicate here, so
    // the chips have no filter change to be rebuilt by.
    rememberShapefileBufferUnit(nextUnit);
    reconciling.current = true;
    stageBuffer(bufferValue, nextUnit);
    reconciling.current = false;
  }

  useEffect(() => {
    if (!onSelectRef) {
      return;
    }
    // The keyboard zone activates a slot by calling click() on it, so a slot
    // has to hold an element. Each mode segment is a label wrapping its own
    // radio input, and clicking that label selects the segment. The buffer
    // row's two controls sit outside those segments, so each is looked up on
    // its own: the number box itself, and — for the unit pair — the radio
    // behind whichever segment is not selected, since that is the one a click
    // has anything to do. The Apply button is the toolbar's own element,
    // registered after them. All of them carry their own disabled state,
    // which is how the zone knows to step over them while unusable.
    const registered: Maybe<HTMLElement>[] = FILTER_MODE_OPTIONS.map((option, index) =>
      optionsRef.current?.querySelectorAll<HTMLElement>(".ant-segmented-item").item(index)
    );
    const unitSegments = Array.from(
      bufferRef.current?.querySelectorAll<HTMLInputElement>(
        ".ant-segmented-item-input"
      ) ?? []
    );
    registered.push(
      bufferInputEl(),
      unitSegments.find((segment) => !segment.checked) ?? unitSegments[0],
      applyRef.current
    );
    registered.forEach((el, index) => {
      onSelectRef(baseToolbarIndex + index, el);
    });
    // Only slots this block actually filled are cleared on the way out, so a
    // slot it never claimed is left to whoever owns it.
    return () => {
      registered.forEach((el, index) => {
        if (el) {
          onSelectRef(baseToolbarIndex + index, null);
        }
      });
    };
  }, [onSelectRef, baseToolbarIndex, activeUnit]);

  const focusedOption =
    toolbarActive && toolbarFocusedIndex !== null
      ? toolbarFocusedIndex - baseToolbarIndex
      : null;

  // Whether the Filter button may be pressed. Two comparisons, so it is not
  // memoized (component-development guidance). The button's own enabled state
  // is the only pending affordance the band shows (FR-013), so nothing else is
  // derived here: a staged change that cannot be applied — a shapefile with no
  // usable name (FR-015) — correctly shows a disabled button.
  const canApply = canApplyShapefileDraft(shapefileName, draft, mode, bufferMeters);

  /**
   * The only path that writes the filter. Flushing the staging function
   * first — rather than reading `draft` from render state — means clicking
   * Apply while the box still has a pending stage in flight (a click blurs
   * the box, which stages; a pause may also still be pending) always applies
   * the value the box is showing, never one render behind it (D-07).
   */
  function handleApply() {
    const staged = stageBufferNow();
    if (!canApplyShapefileDraft(shapefileName, staged, mode, bufferMeters)) {
      return;
    }
    applyDraft(staged);
  }

  return (
    <Wrapper>
      <OptionLabel id={labelId}>Shapefile Filtering</OptionLabel>
      <OptionGroup ref={optionsRef}>
        <SegmentedToggle
          block
          variant={SegmentedVariant.Base}
          aria-labelledby={labelId}
          value={draft.mode}
          options={FILTER_MODE_OPTIONS.map((option, index) => ({
            value: option.mode,
            label: option.label,
            // Focus is drawn on the segment itself, and the segment is picked
            // by its position in this list — the same order the slots above
            // were registered in. Position in the rendered markup would not
            // do: antd slides a thumb element in among the segments while the
            // selection animates.
            className: index === focusedOption ? "keyboard-focused" : undefined
          }))}
          onChange={(value) =>
            setDraft((d) => ({ ...d, mode: value as ShapefileFilterMode }))
          }
        />
      </OptionGroup>
      <BufferRow ref={bufferRef}>
        <BufferLabel $dead={!filtering}>Buffer</BufferLabel>
        {/* The browser's own number box, so that the arrows it draws step the
            number the way they do everywhere else. Its max stops those arrows
            at the furthest the unit beside it takes, and a longer number lands
            on that same distance as it is typed rather than waiting for a
            stage to cut it down. Its step is a distance the filter can tell
            apart, so a click always moves the buffer. */}
        <Input
          aria-label="Buffer distance"
          className={focusedOption === BUFFER_INPUT_SLOT ? "keyboard-focused" : undefined}
          disabled={!filtering}
          type="number"
          min={0}
          max={unitMax(activeUnit)}
          step={unitStep(activeUnit)}
          size="small"
          value={bufferValue ?? ""}
          onChange={(event) => {
            const typed = parseFloat(event.target.value);
            showsAppliedBuffer.current = false;
            const value = isFinite(typed) ? Math.min(typed, unitMax(activeUnit)) : null;
            setBufferValue(value);
            stageBuffer(value, activeUnit);
            stageBufferAfterAPause();
          }}
          onKeyDown={blockNonNumericKeys}
          onPaste={onBufferPaste}
          onBlur={stageBufferNow}
          onPressEnter={stageBufferNow}
          // The keyboard zone activates its slot by clicking it, and a click
          // is not what puts the caret in a box: a click carried out in code
          // moves no focus at all. A pointer click focuses it anyway, so
          // this only ever matters for the keyboard.
          onClick={(event) => event.currentTarget.focus()}
        />
        {/* Two units, one switch: the pair the user reads in, with the one
            being typed in filled. */}
        <UnitSwitch
          role="group"
          aria-label="Buffer unit"
          className={focusedOption === BUFFER_UNIT_SLOT ? "keyboard-focused" : undefined}
          disabled={!filtering}
          value={activeUnit}
          options={unitPair.map((pairUnit) => ({ value: pairUnit, label: pairUnit }))}
          onChange={(value) => onUnitChange(value as ShapefileBufferLabelUnit)}
        />
        <Button
          size="small"
          type="primary"
          disabled={!canApply}
          ref={applyRef}
          onClick={handleApply}
          icon={<Icon path={mdiFilter} size={1.0} />}
          className={focusedOption === APPLY_SLOT ? "keyboard-focused" : undefined}>
          Filter
        </Button>
      </BufferRow>
    </Wrapper>
  );
}

export default ShapefileFieldOptions;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding-top: var(--space-2);
`;

// Mirrors the field-group titles in the list below (FieldGroupTitle), so the
// section reads at the same level as the shapefile's own group header.
const OptionLabel = styled.span`
  font-size: 1.4rem;
  font-weight: var(--fontWeightBold);
  color: var(--color-text-60);
`;

// The segments are antd's own elements, so keyboard focus is drawn onto the
// one the zone is sitting on rather than onto a wrapper of our own. The
// outline itself comes from the field picker's shared keyboard-focused rule.
const OptionGroup = styled.div`
  width: 100%;
`;

// One row for the whole buffer: label, number, unit switch and the Filter button.
// The label, the switch and the button keep their natural widths and the
// number takes whatever is left, so the button costs the box some room rather
// than a line of its own — the band was a third taller for a button sitting
// alone under it. The box is floored at a width that still shows four digits
// and the browser's own steppers, which is what a buffer needs.
const BufferRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding-top: var(--space-1);

  .ant-input {
    flex: 1;
    min-width: 72px;
    height: 30px;
  }

  /* antd lays a button's contents out inline, which hangs the icon off the
     text baseline rather than centring it against the cap height, and its
     icon-to-label margin only applies to its own anticon wrapper — an mdi svg
     gets none. Flex puts the icon and the label on one centred line and gives
     them a gap of their own. */
  .ant-btn {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
  }
`;

// Greys out with the controls beside it, so the whole row reads as one thing
// that is not available yet rather than as a live label over dead controls.
const BufferLabel = styled.span<{ $dead: boolean }>`
  font-size: 1.4rem;
  margin-right: var(--space-1);
  color: ${(props) => (props.$dead ? "var(--color-text-20)" : "var(--color-text-60)")};
`;

// The pill look the mode switcher above it gives up: a small track with the
// unit in use filled in.
const UnitSwitch = styled(SegmentedToggle)`
  flex: none;
`;
